import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from "@mui/material";
import { skipToken } from "@reduxjs/toolkit/query";
import { FormEvent, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";

import { useEcrsForVidQuery } from "api/memberPortal/ecr/ecr.api";
import { useGetFileMutation } from "api/memberPortal/files/files.api";
import { useListAllPoliciesQuery } from "api/memberPortal/policies/policies.api";
import {
  useFinalizeProductFeedbackMutation,
  useSaveProductFeedbackMutation,
  useWithdrawProductFeedbackMutation,
} from "api/memberPortal/product/feedback/feedback.api";
import { Product } from "api/memberPortal/project/product/product.types";
import { PRODUCT_STATUSES } from "api/memberPortal/project/product/productStatuses.type";
import { useGetPublicLabgramsQuery } from "api/public/labgrams/labgrams.api";
import { useAppForm } from "components/Forms/AppForm";
import { useLoading } from "components/UI/LoadingSpinner/UseLoading";

import { defaultProficiencyFeedbackValues } from "../ProficiencyFeedbackForm.default";
import {
  ProficiencyFeedbackFormSchema,
  ProficiencyFeedbackFormValues,
  toFeedbackPayload,
} from "../ProficiencyFeedbackForm.schema";

import EvaluationSummaryStats from "./components/EvaluationSummaryStats";
import CommentsFormText from "./components/form/Comments";
import PotentialNonConformitiesFormText from "./components/form/PotentialNonConformities";
import QuestionsForm from "./components/form/Questions";
import RatingsForm from "./components/form/Ratings";
import StatementOfProficiencyFormSelect from "./components/form/StatementOfProficiencyFormSelect";
import ProductInfoSection from "./components/ProductInfoSection";

type SubmitAction = "draft" | "finalize" | "withdrawn";

interface ProficiencyFeedbackFormProps {
  feedbackForm: ProficiencyFeedbackFormValues | undefined;
  product: Product;
  readonly: boolean;
  pdfFileId: number | null;
}

const ProficiencyFeedbackForm = ({
  feedbackForm,
  product,
  readonly,
  pdfFileId,
}: ProficiencyFeedbackFormProps) => {
  const viewOnly = false;
  const [triggerDraft] = useSaveProductFeedbackMutation();
  const [triggerSubmit] = useFinalizeProductFeedbackMutation();
  const [triggerWithdraw] = useWithdrawProductFeedbackMutation();

  const [formError, setFormError] = useState("");

  const { id: parentId } = useParams();

  const { data: ecrs } = useEcrsForVidQuery(parentId ?? skipToken);
  const { data: policies } = useListAllPoliciesQuery();
  const { data: labgrams } = useGetPublicLabgramsQuery();

  const { showLoadingSpinner, hideLoadingSpinner } = useLoading();

  const navigate = useNavigate();

  const [triggerGetFile] = useGetFileMutation();

  const isWithdrawnOrTerminated =
    product.status === PRODUCT_STATUSES.WITHDRAWN ||
    product.status === PRODUCT_STATUSES.TERMINATED;

  const pfForm = useAppForm({
    defaultValues: feedbackForm ?? defaultProficiencyFeedbackValues,
    validators: {
      onSubmit: ProficiencyFeedbackFormSchema,
    },
    onSubmit: async ({ value }) => {
      if (!parentId) return;
      showLoadingSpinner("Saving Proficiency Feedback Form");

      try {
        const body = toFeedbackPayload(value);

        if (value.formState === "finalize") {
          await triggerSubmit({ parentId, body });
        } else if (value.formState === "withdrawn") {
          await triggerWithdraw({ parentId, body });
        } else {
          await triggerDraft({ parentId, body });
        }

        navigate("..");
      } finally {
        hideLoadingSpinner();
      }
    },

    onSubmitInvalid: async () => {
      setFormError("Please fix the highlighted errors before submitting.");
    },
  });

  const handleSubmit = (e: FormEvent, action: SubmitAction) => {
    e.preventDefault();
    pfForm.reset({
      ...pfForm.state.values,
      formState: action,
    } as ProficiencyFeedbackFormValues);
    pfForm.handleSubmit();
  };

  const handleDownload = async (fileId: number) => {
    if (!fileId) return;
    showLoadingSpinner("Downloading");
    await triggerGetFile(fileId);
    hideLoadingSpinner();
  };

  return (
    <Dialog open maxWidth="md" fullWidth>
      <form onSubmit={(e) => e.preventDefault()}>
        <DialogTitle>CCTL Proficiency Report</DialogTitle>

        <DialogContent>
          <pfForm.AppForm>
            <Stack gap={1}>
              {!viewOnly && (
                <Typography
                  variant="body2"
                  sx={{ color: "text.secondary", mt: 1 }}
                >
                  Fields marked with an asterisk (*) are required.
                </Typography>
              )}
              {/* Product Information */}
              <pfForm.FormSection title="Product Information">
                <ProductInfoSection
                  feedbackForm={feedbackForm}
                  product={product}
                />
              </pfForm.FormSection>

              {/* Statement of Proficiency */}
              <pfForm.FormSection title="Statement of Proficiency">
                <StatementOfProficiencyFormSelect
                  form={pfForm}
                  readonly={readonly}
                />
              </pfForm.FormSection>

              {/* Potential Non-Conformities */}
              <pfForm.FormSection title="Potential Non-Conformities">
                <PotentialNonConformitiesFormText
                  form={pfForm}
                  readonly={readonly}
                />
              </pfForm.FormSection>

              {/* Questions */}
              <pfForm.FormSection title="PLEASE ANSWER THE FOLLOWING QUESTIONS TO THE BEST OF YOUR ABILITY:">
                <EvaluationSummaryStats
                  product={product}
                  feedbackForm={feedbackForm}
                />
                <QuestionsForm
                  form={pfForm}
                  readonly={readonly}
                  ecrs={ecrs || []}
                  policies={policies || []}
                  labgrams={labgrams || []}
                />
              </pfForm.FormSection>

              {/* Rating */}
              <pfForm.FormSection title="PLEASE RATE THE FOLLOWING QUESTIONS">
                <RatingsForm form={pfForm} readonly={readonly} />
              </pfForm.FormSection>

              {/* Comments */}
              <pfForm.FormSection title="Comments">
                <CommentsFormText form={pfForm} readonly={readonly} />
              </pfForm.FormSection>
            </Stack>
          </pfForm.AppForm>

          {formError && (
            <Alert severity="error" sx={{ mt: 1 }}>
              {formError}
            </Alert>
          )}
        </DialogContent>

        <DialogActions>
          <Button variant="outlined" component={Link} to="..">
            Cancel
          </Button>
          {pdfFileId && (
            <Button
              variant="contained"
              onClick={() => handleDownload(pdfFileId)}
            >
              Download PDF
            </Button>
          )}

          {!readonly && (
            <>
              <Button
                variant="contained"
                color="primary"
                type="submit"
                onClick={(e) => handleSubmit(e, "draft")}
              >
                Save As Draft
              </Button>
              {(product.validator_posting_date || isWithdrawnOrTerminated) && (
                <Button
                  variant="contained"
                  color="success"
                  type="submit"
                  onClick={(e) =>
                    handleSubmit(
                      e,
                      isWithdrawnOrTerminated ? "withdrawn" : "finalize",
                    )
                  }
                >
                  Submit
                </Button>
              )}
            </>
          )}
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default ProficiencyFeedbackForm;
